import { useState, type FormEvent } from "react";
import { api } from "../api.js";
import { HeroBrand, LocaleToggle, PasswordField, ThemeToggle, useLocale } from "../ui.js";
import type { AuthView } from "../App.js";

export function ForgotPage({ onNavigate }: { onNavigate: (v: AuthView) => void }) {
  const { tr } = useLocale();
  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState<"email" | "whatsapp">("email");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    await api.requestReset(email.trim(), channel);
    setBusy(false);
    setInfo(tr("codeSent"));
    setStep("reset");
  }

  async function doReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await api.reset(email.trim(), code.trim(), newPassword);
    setBusy(false);
    if (res.ok) {
      setDone(true);
      setInfo(null);
    } else {
      setError(tr("genericError"));
    }
  }

  return (
    <main className="ss-login">
      <HeroBrand />
      <div className="ss-login-form-wrap">
        <form className="ss-login-card" onSubmit={step === "request" ? requestCode : doReset}>
          <div className="ss-login-toprow">
            <ThemeToggle />
            <LocaleToggle />
          </div>
          <h2>{tr("forgot")}</h2>
          <p className="sub">{tr("forgotSub")}</p>

          <div className="ss-field">
            <label htmlFor="fp-email">{tr("email")}</label>
            <input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={step === "reset"} />
          </div>

          {step === "request" ? (
            <div className="ss-field">
              <label htmlFor="fp-channel">{tr("channel")}</label>
              <select id="fp-channel" value={channel} onChange={(e) => setChannel(e.target.value as "email" | "whatsapp")}>
                <option value="email">{tr("channelEmail")}</option>
                <option value="whatsapp">{tr("channelWhatsapp")}</option>
              </select>
            </div>
          ) : (
            <>
              <div className="ss-field">
                <label htmlFor="fp-code">{tr("code")}</label>
                <input id="fp-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required />
              </div>
              <PasswordField id="fp-new" label={tr("newPassword")} value={newPassword} onChange={setNewPassword} autoComplete="new-password" minLength={8} />
            </>
          )}

          {info ? <div className="msg info">{info}</div> : null}
          {error ? <div className="msg error">{error}</div> : null}
          {done ? <div className="msg ok">{tr("saved")}</div> : null}

          {!done ? (
            <button className="ss-login-submit" type="submit" disabled={busy}>
              {step === "request" ? tr("sendCode") : tr("resetPassword")}
            </button>
          ) : null}
          <div className="btn-row">
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("login")}>
              {tr("toLogin")}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
